import { Box, Divider, Heading, RadioGroup, Sheet } from '@hover/blueprint';
import { useRadioGroup } from '@hover/blueprint/chakra';
import React, { useCallback, useEffect } from 'react';

import {
  alwaysCreateNewProject,
  closeUploadSheetAfterUpload,
} from 'src/modules/homeownerApp/components/UploadImage';
import type { UploadImageSubmitProps } from 'src/modules/homeownerApp/components/UploadImage';
import { ProjectSelectionTile } from 'src/modules/homeownerApp/components/UploadImage/ProjectSelectionTile';
import { VERTICAL_DRAWER_PADDING_PIXELS } from 'src/modules/homeownerApp/components/UploadImage/SpaceTypeSheetContent';
import { UploadImageBox } from 'src/modules/homeownerApp/components/UploadImage/UploadImageBox';
import { formatSpaceType } from 'src/modules/homeownerApp/components/Utils';
import { useViewportSize } from 'src/modules/homeownerApp/hooks';
import { Button } from 'src/shared/components';
import {
  type Project,
  useGetProjectsQuery,
} from 'src/shared/graphql/generated';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';

type ProjectConfirmationProps = {
  ctaText: string;
  onNext: (props: UploadImageSubmitProps) => void;
  isLoading: boolean;
  imageUrl: string;
  trackingData: object;
  project: Project;
  spaceType: string;
};

const NEW_PROJECT_RADIO_VALUE = 'new';

export const ProjectConfirmation = ({
  imageUrl,
  onNext,
  isLoading,
  ctaText,
  trackingData,
  project: currentProject,
  spaceType,
}: ProjectConfirmationProps) => {
  const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
  const { height: viewportHeight } = useViewportSize();
  const { projects } = useGetProjectsQuery().data ?? {};
  const projectRadioValue = String(currentProject?.id);

  const { getRadioProps, value } = useRadioGroup({
    defaultValue: projectRadioValue,
  });

  const handleSubmit = useCallback(() => {
    if (value === NEW_PROJECT_RADIO_VALUE) {
      closeUploadSheetAfterUpload(false);
      alwaysCreateNewProject(true);
    }

    onNext({ projectId: Number(value) });
  }, [onNext, value]);

  useEffect(() => {
    document
      .querySelector(
        `[data-testid="project-confirmation-radio-${projectRadioValue}"]`,
      )
      ?.scrollIntoView({ behavior: 'smooth' });
  }, [projectRadioValue]);

  return (
    <Sheet.Contents contentKey="project-confirmation">
      <Box
        alignItems={{ base: 'center' }}
        backgroundColor="neutral.0"
        data-testid="upload-image-box-wrapper"
        flexDirection={{ base: 'column', desktop: 'row-reverse' }}
        gap={{ tablet: 500 }}
        height={{
          base: `calc(${viewportHeight} - ${VERTICAL_DRAWER_PADDING_PIXELS}px)`,
        }}
        overflow="auto"
        pb={{ desktop: 700 }}
        pt={{ base: 400, tablet: 'unset' }}
        px={{ desktop: 500 }}
      >
        <UploadImageBox
          borderRadius={400}
          height="100%"
          imageUrl={imageUrl}
          maxHeight={{ base: '240px', desktop: '575px' }}
          minHeight={{ base: 600, desktop: 750 }}
        />
        <Box
          data-testid="upload-image-form-wrapper"
          flex={1}
          flexDirection="column"
          justifyContent={{ desktop: 'center' }}
          w={{ base: 1, tablet: 'unset' }}
        >
          <Heading
            alignSelf={{
              base: 'flex-start',
              tablet: 'center',
              desktop: 'flex-start !important',
            }}
            fontWeight={700}
            m="0 !important"
            maxW={{ base: '280px', tablet: 750, desktop: '440px' }}
            pb={{ base: 500, tablet: 700, desktop: 600 }}
            pt={{ base: 600, tablet: 800, desktop: 'unset' }}
            size={{ base: 'large', tablet: 700 }}
            textAlign={{ base: 'left', tablet: 'center', desktop: 'left' }}
          >
            {t('uploadImage.addToAProject')}
          </Heading>
          <RadioGroup
            data-testid="project-confirmation-radios"
            defaultValue={projectRadioValue}
            flex={1}
            flexBasis={700}
            overflow="auto"
          >
            {projects?.map(project => (
              <Box
                data-testid={`project-confirmation-radio-${String(project.id)}`}
                flexDirection="column"
                key={project.id}
              >
                <ProjectSelectionTile
                  item={{
                    label: project.name,
                    description:
                      formatSpaceType(
                        project.addables?.[0]?.spaceType ?? undefined,
                      ) || project.name,
                    value: String(project.id),
                    image:
                      project.addables?.[0]?.aiGeneratedImage
                        .generatedImagePath,
                  }}
                  {...getRadioProps({ value: String(project.id) })}
                />
                <Divider my={200} />
              </Box>
            ))}
            <ProjectSelectionTile
              item={{
                label: 'Create new project',
                description: formatSpaceType(spaceType),
                value: NEW_PROJECT_RADIO_VALUE,
              }}
              {...getRadioProps({ value: NEW_PROJECT_RADIO_VALUE })}
            />
          </RadioGroup>
          <Box
            alignItems={{
              base: 'flex-start',
              tablet: 'center',
              desktop: 'flex-start',
            }}
            data-testid="upload-submit-wrapper"
            flexDirection="column"
            gap={{ base: 400, tablet: 600, desktop: 600 }}
            justifyContent={{ base: 'flex-end', desktop: 'flex-start' }}
            minWidth="100%"
            mt="auto"
            pb={{ base: 400, desktop: 'unset' }}
            pt={{ base: 500, tablet: 600, desktop: 600 }}
          >
            <Button
              data-testid="upload-image-submit-button"
              height={500}
              isLoading={isLoading}
              mb={{ base: 50, tablet: 'unset', desktop: 'unset' }}
              mt={{ tablet: 200, desktop: 'unset' }}
              onClick={handleSubmit}
              optionalTrackingProperties={{
                ...trackingData,
                button_location: 'bottom-main-page',
                track_id: 'tos-generate-images',
                primary_cta: true,
              }}
              width={{ base: '100%', tablet: '195px' }}
            >
              {ctaText}
            </Button>
          </Box>
        </Box>
      </Box>
    </Sheet.Contents>
  );
};
